import React, { memo, type ReactNode, useCallback, useEffect, useState } from 'react';
import isNil from 'lodash/isNil';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag/src/index.tsx';
import { FieldInlineEditStateLess } from '@atlassian/jira-issue-field-inline-edit/src/ui/index.tsx';
import {
	CONTENT_AREA,
	type CONTENT_REQUEST_AREA,
	type CONTEXT_AREA,
} from '@atlassian/jira-issue-layout-common-constants/src/index.tsx';
import {
	ContextualAnalyticsData,
	fireOperationalAnalytics,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import { CmdbFieldObjectPicker } from '@atlassian/jira-servicedesk-cmdb-field-object-picker/src/ui/index.tsx';
import {
	LimitData,
	MAX_CMDB_OBJECTS_PER_FIELD,
} from '@atlassian/jira-servicedesk-common/src/ui/components/limit-data/index.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { isPremiumInstance } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import { graphModalExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import { InsightObjectGraphModal } from '@atlassian/jira-servicedesk-insight-object-graph/src/ui/index.tsx';
import { transformBackendValueToCmdbObject } from '@atlassian/jira-servicedesk-insight-object-services/src/services/utils.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { USER_JSM_PROJECT_ROLE } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/constants.tsx';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useHoverState } from '../common/ui/hover-state/index.tsx';
import { convertValueToCmdbObjectType, saveFieldCall, sortCmdbObjects } from '../common/utils.tsx';
import { useCmdbObjectField } from '../services/cmd-object-field/index.tsx';
import { useSchemaDetails } from '../services/schema-details/index.tsx';
import { ActionButtons } from './action-buttons/index.tsx';
import { CmdbObjectCard } from './card/index.tsx';
import { CmdbFieldError } from './error/index.tsx';
import { CmdbFieldLabel } from './label/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const actionSubject = 'cmdbObjectInlineEdit';

export const CmdbObjectMain = memo<Props>((props: Props) => {
	const {
		fieldId,
		issueKey,
		onCancel,
		onEscape,
		onUpdate,
		onEdit,
		label,
		cmdbFieldConfig,
		area,
		issueId,
		wasInsightRequestSuccessful,
		source,
	} = props;

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isFieldExpanded, setIsFieldExpanded] = useState<boolean>(true);

	const [savedValueObjects, setSavedValueObjects] = useState<CmdbObject[]>([]);
	const [displayedObjects, setDisplayedObjects] = useState<CmdbObject[]>([]);

	const [currentOpenObject, setCurrentOpenObject] = useState<CmdbObject | undefined>();
	const [expandedObjects, setExpandedObjects] = useState<string[]>([]);
	const [isHovered, hoverProps] = useHoverState();

	const tenantContext: TenantContext = useTenantContext();
	const { workspaceId } = useWorkspaceContext();
	const hasPremium = isPremiumInstance(tenantContext);
	const isCmdbEnabled = isCMDBEnabledInEnvironment(tenantContext.cmdbDisabledInFedRAMP);
	const hasSchema = !isNil(cmdbFieldConfig && cmdbFieldConfig.objectSchemaId) || false;
	const isObjectListUnenriched = wasInsightRequestSuccessful === false;
	const hasInvalidWorkspaceId =
		Array.isArray(props.value) && props.value.some((v) => v.id.split(':')[0] !== workspaceId);
	const hasError = !hasPremium || !hasSchema || isObjectListUnenriched || !isCmdbEnabled;
	// props.value goes from undefined to an array when finished loading (even for empty fields it will be an empty array)
	const hasFinishedLoading = props.value !== undefined;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ data: serviceDeskContextData, error: serviceDeskContextError }] =
		useServiceDeskContext();
	const hasFetchedServiceDeskContext = !!serviceDeskContextData || !!serviceDeskContextError;

	const schemaDetails = useSchemaDetails(workspaceId, cmdbFieldConfig?.objectSchemaId);
	const hasFetchedSchemaDetails =
		schemaDetails.loadingState === 'success' || schemaDetails.loadingState === 'error';

	const isJsmAgent =
		serviceDeskContextData?.userRole === USER_JSM_PROJECT_ROLE.AGENT_ROLE ||
		serviceDeskContextData?.userRole === USER_JSM_PROJECT_ROLE.AGENT_PROJECT_ADMIN_ROLE
			? 'true'
			: 'false';

	const onSuccess = (newValue: Array<CmdbObject>) => {
		onUpdate && onUpdate(newValue);
		fireOperationalAnalytics(createAnalyticsEvent({}), 'insightField updateSucceeded', {
			isJsmAgent,
		});
	};

	const [{ value, error: cmdbObjectFieldError, fieldConfig }, { saveValue, resetError }] =
		useCmdbObjectField({
			issueKey: toIssueKey(issueKey),
			fieldKey: fieldId,
			onSuccess,
			onSaveField: saveFieldCall,
		});

	// On mount: we need to transform the initial value given to us.
	useEffect(() => {
		const initialTypedValue = convertValueToCmdbObjectType(value);
		setSavedValueObjects(initialTypedValue.map(transformBackendValueToCmdbObject));
	}, [value]);

	// Fire secondary analytics events with extra details once we've fetch extra data
	useEffect(() => {
		const initialTypedValue = convertValueToCmdbObjectType(value);
		if (!hasError && hasFetchedServiceDeskContext && hasFetchedSchemaDetails) {
			const attributes = {
				objectIds: initialTypedValue.map((object) => ({
					id: object.id,
					isDemoService: false,
				})),
				numberOfObjects: initialTypedValue.length,
				// Distinguish between Insight vs Service views of this component
				source,
				isJsmAgent,
				atlassianTemplateId: schemaDetails.data?.atlassianTemplateId,
			};
			fireUIAnalytics(createAnalyticsEvent({}), 'insightField viewed', attributes);
		}
	}, [
		createAnalyticsEvent,
		hasError,
		hasFetchedSchemaDetails,
		hasFetchedServiceDeskContext,
		isJsmAgent,
		schemaDetails.data?.atlassianTemplateId,
		source,
		value,
	]);

	// When the saved objects are updated (on initial load, on save), we want to update our list of displayed objects.
	useEffect(() => {
		setDisplayedObjects(sortCmdbObjects(savedValueObjects));
	}, [savedValueObjects]);

	const isFieldEditable = fieldConfig ? fieldConfig.isEditable : false;

	useEffect(() => {
		if (cmdbObjectFieldError) {
			setIsEditing(true);
			fireOperationalAnalytics(createAnalyticsEvent({}), 'insightField updateFailed', {
				isJsmAgent,
			});
		}
	}, [cmdbObjectFieldError, createAnalyticsEvent, isJsmAgent]);

	const save = useCallback(
		(analyticsEvent: UIAnalyticsEvent, updatedObjects: CmdbObject[] = displayedObjects) => {
			saveValue(updatedObjects, null, analyticsEvent);
			setSavedValueObjects(displayedObjects);
			setIsEditing(false);
			fireUIAnalytics(analyticsEvent);
		},
		[saveValue, displayedObjects],
	);

	const cancel = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			resetError();
			setIsEditing(false);
			setDisplayedObjects(savedValueObjects ?? []);
			fireUIAnalytics(analyticsEvent);
		},
		[resetError, savedValueObjects],
	);

	const onCancelRequest = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			cancel(analyticsEvent);
			onCancel && onCancel(analyticsEvent);
		},
		[cancel, onCancel],
	);

	const onConfirmRequest = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			save(analyticsEvent);
		},
		[save],
	);

	const onEditRequested = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			setIsEditing(true);
			setIsFieldExpanded(true);
			fireUIAnalytics(analyticsEvent, 'editInsightField');

			if (onEdit) {
				onEdit(analyticsEvent);
			}
		},
		[onEdit],
	);

	const onEscapeRequest = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			cancel(analyticsEvent);
			onEscape && onEscape(analyticsEvent);
		},
		[cancel, onEscape],
	);

	const onChange = useCallback(
		(newValue: CmdbObject[]) => {
			resetError();
			setDisplayedObjects(sortCmdbObjects([...displayedObjects, ...newValue]));
		},
		[resetError, displayedObjects],
	);

	const isMultipleValuesEnabled = (cmdbFieldConfig && cmdbFieldConfig.multiple) || false;

	const renderEditView = (limit: number) => {
		if (!displayedObjects || !fieldConfig) {
			return null;
		}

		return (
			<CmdbFieldObjectPicker
				autoCompleteUrl={fieldConfig && fieldConfig.autoCompleteUrl}
				displayedValues={null}
				selectedValues={displayedObjects}
				multiple={isMultipleValuesEnabled}
				attributesToDisplayInSearch={
					cmdbFieldConfig && cmdbFieldConfig.attributesIncludedInAutoCompleteSearch
				}
				onChange={onChange}
				issueId={issueId}
				limit={limit}
				autoFocus
				menuPosition={null}
				testId="issue-field-cmdb-object.ui.cmdb-object-edit"
			/>
		);
	};

	const renderReadView = () => null;

	const onDeleteObject = (object: CmdbObject, analyticsEvent: UIAnalyticsEvent) => {
		setDisplayedObjects(displayedObjects.filter((candidate) => candidate.id !== object.id));
		fireUIAnalytics(analyticsEvent, 'insightObjectRemoved');
	};

	const onCloseObjectDialog = () => {
		setCurrentOpenObject(undefined);
	};

	const onExpandCollapseClick = () => {
		setIsFieldExpanded(!isFieldExpanded);
	};

	const renderObjectCard = (object: CmdbObject) => {
		const isExpanded = expandedObjects.includes(object.id);

		return (
			<CmdbObjectCard
				object={object}
				attributesToDisplay={(cmdbFieldConfig && cmdbFieldConfig.attributesDisplayedOnIssue) || []}
				onDelete={onDeleteObject}
				isEditing={isEditing}
				isExpanded={isExpanded}
				onViewObjectDetails={(objectToView: CmdbObject, analyticsEvent: UIAnalyticsEvent) => {
					graphModalExperience.start();
					fireUIAnalytics(analyticsEvent, 'insightObjectViewFullDetailsAndGraph');
					setCurrentOpenObject(objectToView);
				}}
				onExpandCollapseClick={(analyticsEvent: UIAnalyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'insightObjectToggleShowDetails', {
						action: isExpanded ? 'hiding' : 'showing',
					});
					setExpandedObjects((existing) => {
						if (existing.includes(object.id)) {
							return existing.filter((objectId) => objectId !== object.id);
						}
						return [...existing, object.id];
					});
				}}
			/>
		);
	};

	const renderInlineErrorMessage = () => {
		if (!hasError) return null;

		return (
			<CmdbFieldError
				fieldId={fieldId}
				hasSchema={hasSchema}
				hasPremium={hasPremium}
				isObjectListUnenriched={isObjectListUnenriched}
				hasInvalidWorkspaceId={hasInvalidWorkspaceId}
				isAdmin={tenantContext.isAdmin}
				isCmdbEnabled={isCmdbEnabled}
			/>
		);
	};

	if (!hasFinishedLoading) {
		return null;
	}

	return (
		<ContextualAnalyticsData sourceName="insightField" sourceType={SCREEN}>
			<FireScreenAnalytics />
			<Wrapper
				onMouseOver={!isEditing ? hoverProps.onMouseOver : undefined}
				onFocus={!isEditing ? hoverProps.onFocus : undefined}
				onMouseOut={!isEditing ? hoverProps.onMouseOut : undefined}
				onBlur={!isEditing ? hoverProps.onBlur : undefined}
				area={area}
			>
				<CmdbFieldLabel
					isEditing={isEditing}
					onEditModeClick={onEditRequested}
					values={displayedObjects}
					isParentHovered={isEditing || isHovered}
					isExpanded={isFieldExpanded}
					onExpandCollapseClick={onExpandCollapseClick}
					inlineErrorMessage={renderInlineErrorMessage()}
					isEditable={isFieldEditable}
					issueKey={issueKey}
					fieldId={fieldId}
					testId="issue-field-cmdb-object.ui.field-label"
				>
					{label}
				</CmdbFieldLabel>
				{!hasError &&
					isFieldExpanded &&
					displayedObjects.map((object, index) => (
						<CardsWrapper
							key={object.id}
							isFirst={index === 0}
							isLast={index === displayedObjects.length - 1}
						>
							{renderObjectCard(object)}
						</CardsWrapper>
					))}
				<LimitData limitProperty={MAX_CMDB_OBJECTS_PER_FIELD}>
					{(limit) => (
						<>
							{isEditing && (
								<>
									<FieldContainer>
										<FieldInlineEditStateLess
											actionSubject={actionSubject}
											label={label}
											fieldId={fg('one_event_rules_them_all_fg') ? fieldId : undefined}
											isEditable={isFieldEditable}
											isEditing={isEditing}
											isLabelHidden
											readView={renderReadView()}
											editView={isFieldEditable ? renderEditView(limit) : null}
											onCancel={onCancelRequest}
											onConfirm={onConfirmRequest}
											onEdit={onEditRequested}
											onEscape={onEscapeRequest}
											isFitContainerWidthReadView
											areActionButtonsHidden
											isConfirmOnBlurDisabled
										/>
									</FieldContainer>
									<ActionButtonWrapper
										data-testid="issue-field-cmdb-object.ui.action-buttons"
										testId="issue-field-cmdb-object.ui.action-buttons"
									>
										<ActionButtons
											isConfirmDisabled={
												(!isMultipleValuesEnabled && displayedObjects.length > 1) ||
												displayedObjects.length > limit
											}
											onConfirm={onConfirmRequest}
											onCancel={onCancelRequest}
										/>
									</ActionButtonWrapper>
									<ErrorFlag
										error={cmdbObjectFieldError}
										title={messages.errorTitleCmdbAssets}
										description={messages.errorMessage}
									/>
								</>
							)}
						</>
					)}
				</LimitData>
			</Wrapper>
			<InsightObjectGraphModal
				objectId={currentOpenObject?.objectId}
				objectTypeId={currentOpenObject?.objectType.objectTypeId}
				onClose={onCloseObjectDialog}
				source="insight"
				testId="issue-field-cmdb-object.ui.object-graph-modal"
				isObjectGraphOpen={!!currentOpenObject}
			/>
		</ContextualAnalyticsData>
	);
});

const Wrapper = ({
	onMouseOver,
	onFocus,
	onMouseOut,
	onBlur,
	area,
	children,
}: {
	onMouseOver?: React.MouseEventHandler<HTMLElement>;
	onFocus?: React.FocusEventHandler<HTMLElement>;
	onMouseOut?: React.MouseEventHandler<HTMLElement>;
	onBlur?: React.FocusEventHandler<HTMLElement>;
	area: typeof CONTEXT_AREA | typeof CONTENT_AREA | typeof CONTENT_REQUEST_AREA;
	children?: ReactNode;
}) => (
	<Box
		onMouseOver={onMouseOver}
		onFocus={onFocus}
		onMouseOut={onMouseOut}
		onBlur={onBlur}
		xcss={[wrapperStyles, area === CONTENT_AREA && wrapperAreaStyles]}
	>
		{children}
	</Box>
);

const CardsWrapper = ({
	isFirst,
	isLast,
	children,
}: {
	isFirst: Boolean;
	isLast: Boolean;
	children?: ReactNode;
}) => (
	<Box xcss={[isFirst && cardsWrapperFirstStyles, isLast && cardsWrapperLastStyles]}>
		{children}
	</Box>
);

const FieldContainer = ({ children }: { children?: ReactNode }) => (
	<Box xcss={fieldContainerStyles}>{children}</Box>
);

const ActionButtonWrapper = ({ testId, children }: { testId?: string; children?: ReactNode }) => (
	<Box testId={testId} xcss={actionButtonWrapperStyles}>
		{children}
	</Box>
);

const wrapperStyles = xcss({
	position: 'relative',
	marginBottom: 'space.200',
});

const wrapperAreaStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'border.radius.050',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
});

const fieldContainerStyles = xcss({
	marginRight: 'space.negative.100',
	marginTop: 'space.150',
	width: '100%',
});

const actionButtonWrapperStyles = xcss({
	position: 'absolute',
	top: `calc(100% + ${token('space.100')})`,
	right: token('space.0'),
	zIndex: 'dialog',
});

const cardsWrapperFirstStyles = xcss({
	marginTop: 'space.150',
});

const cardsWrapperLastStyles = xcss({
	marginBottom: 'space.100',
});
